import React from 'react';
import { Bookmark } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSavedGigs, toggleBookmark } from '../../services/gig.service';
import { messagec } from '../../assets/assets';
import { useNavigate } from 'react-router-dom';

const truncateTitle = (t) => (t && t.length > 24 ? t.substring(0, 24) + '...' : t);

const getLowestPrice = (pricing) => {
  if (!pricing) return null;
  const prices = [
    pricing.basic?.price,
    pricing.standard?.price,
    pricing.advanced?.price,
  ].filter((p) => p !== undefined && p !== null);
  return prices.length > 0 ? Math.min(...prices) : null;
};

const SavedProjectList = ({ searchQuery = '' }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['savedGigs'],
    queryFn: getSavedGigs,
    staleTime: 30_000,
  });

  const { mutate: removeSaved } = useMutation({
    mutationFn: (gigId) => toggleBookmark(gigId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['savedGigs'] }),
  });

  const gigs = (data?.gigs ?? []).filter(gig => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const categoryName = Array.isArray(gig.category) 
      ? gig.category[0]?.name 
      : gig.category?.name;
    
    return (
      gig.title?.toLowerCase().includes(query) ||
      categoryName?.toLowerCase().includes(query)
    );
  });

  if (isLoading) {
    return (
      <div className="font-['Poppins'] w-full">
        <h2 className="text-[#174568] dark:text-blue-400 text-[18px] font-medium mb-4">Saved Projects</h2>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-[#1d2026] rounded-[10px] border border-[#DDE1E6] dark:border-[#32353c] p-3 flex items-center animate-pulse">
              <div className="h-[57px] w-12 rounded-md bg-gray-200 dark:bg-gray-700 mr-3 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="font-['Poppins'] w-full">
      <h2 className="text-[#174568] dark:text-blue-400 text-[18px] font-medium mb-4">Saved Projects</h2>

      {gigs.length === 0 ? (
        <div className="bg-white dark:bg-[#1d2026] rounded-[10px] border border-[#DDE1E6] dark:border-[#32353c] p-6 text-center">
          <Bookmark size={32} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No saved projects yet.</p>
          <button
            onClick={() => navigate('/category/all-services')}
            className="mt-3 text-sm text-[#174568] dark:text-blue-400 underline"
          >
            Explore services
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {gigs.map((gig) => {
            const categoryName = Array.isArray(gig.category) ? gig.category[0]?.name : gig.category?.name;
            const lowestPrice = getLowestPrice(gig.pricing);

            return (
              <div
                key={gig._id}
                className="bg-white dark:bg-[#1d2026] rounded-[10px] border-[1px] border-[#DDE1E6] dark:border-[#32353c] p-3 flex items-center shadow-sm"
              >
                <div className="h-[57px] w-12 rounded-md overflow-hidden mr-3 flex-shrink-0">
                  {gig.cover ? (
                    <img src={gig.cover} alt={gig.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-400">No img</div>
                  )}
                </div>

                <div className="flex-grow mr-2">
                  <p className="text-[12px] text-[#174568B2] dark:text-blue-400/70 font-medium">{categoryName || 'Service'}</p>
                  <a href={`/gigs/${gig.slug || gig._id}`}>
                    <h3 className="text-[14px] text-[#174568] dark:text-white hover:underline">{truncateTitle(gig.title)}</h3>
                  </a>

                  <div className="w-full flex justify-between mt-2">
                    <p className="text-xs text-[#000000] dark:text-gray-300">{lowestPrice != null ? `From $${lowestPrice}` : ''}</p>
                    <button
                      onClick={() => removeSaved(gig._id)}
                      title="Remove bookmark"
                      className="text-[#174568] hover:text-red-500 transition-colors"
                    >
                      <Bookmark size={15} className="fill-[#174568] hover:fill-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SavedProjectList;
